import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { io, Socket } from 'socket.io-client';
import { API_CONFIG } from '@/constants/api';
import { Sidebar } from '@/components/common/Sidebar';
import { cancelOnDemandConsultation } from '@/services/consultations';
import { toast } from 'sonner';
import { 
  Loader2, 
  Clock, 
  User, 
  AlertCircle
} from 'lucide-react';

export default function OnDemandWaitingPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [status, setStatus] = useState<'waiting' | 'matched' | 'left'>('waiting');
  const [timer, setTimer] = useState(0);
  const [estimatedTime, setEstimatedTime] = useState<number>(5);
  const [matchedDoctor, setMatchedDoctor] = useState<{ id: string; name: string } | null>(null);
  const [isLeaving, setIsLeaving] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const leftQueueRef = useRef(false);

  const leaveQueue = useCallback(async (redirect = true) => {
    if (leftQueueRef.current) {
      if (redirect) navigate('/dashboard');
      return;
    }
    leftQueueRef.current = true;
    setIsLeaving(true);
    setStatus('left');

    try {
      // REST soft-cancel (source of truth) + WS notify médicos em proposta
      await cancelOnDemandConsultation();
      if (socketRef.current?.connected && user) {
        socketRef.current.emit('cancelQueueRequest', { patientId: user.id });
      }
      sessionStorage.removeItem('onDemandTriage');
    } catch (err) {
      console.error('Error leaving on-demand queue:', err);
      toast.error('Não foi possível confirmar a saída da fila. Tente novamente.');
      leftQueueRef.current = false;
      setStatus('waiting');
      setIsLeaving(false);
      return;
    } finally {
      setIsLeaving(false);
    }

    if (redirect) {
      navigate('/dashboard');
    }
  }, [navigate, user]);

  useEffect(() => {
    if (!user) return;

    const triageData = sessionStorage.getItem('onDemandTriage');
    if (!triageData) {
      navigate('/on-demand/triage');
      return;
    }

    const { reason, symptoms, mode } = JSON.parse(triageData);

    const socketUrl = API_CONFIG.BASE_URL.replace('/api', '');
    const socket = io(`${socketUrl}/consultation`, {
      query: { userId: user.id },
      transports: ['websocket'],
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      if (leftQueueRef.current) return;
      socket.emit('joinOnDemandQueue', {
        patientId: user.id,
        reason,
        symptoms,
        mode: mode || 'TELEMEDICINE',
      });
    });

    socket.on('queueUpdated', (data: { doctorQueueLength: number, patientQueueLength: number }) => {
      const estimatedMinutes = Math.max(
        1,
        Math.ceil(data.patientQueueLength * 5 / Math.max(1, data.doctorQueueLength)),
      );
      setEstimatedTime(estimatedMinutes);
    });

    socket.on('matchFound', (data: { appointmentId: string; doctorId: string }) => {
      if (leftQueueRef.current) return;
      // Impede cancel no unmount ao redirecionar para o chat
      leftQueueRef.current = true;
      setStatus('matched');
      setMatchedDoctor({ id: data.doctorId, name: 'Médico Disponível' });
      sessionStorage.removeItem('onDemandTriage');
      setTimeout(() => {
        navigate(`/chat/${data.appointmentId}`);
      }, 3000);
    });

    socket.on('queueCancelled', () => {
      leftQueueRef.current = true;
      setStatus('left');
      sessionStorage.removeItem('onDemandTriage');
    });

    const interval = setInterval(() => {
      setTimer(prev => prev + 1);
    }, 1000);

    return () => {
      clearInterval(interval);
      // Ao desmontar (navegar para outra página), garante cancelamento se ainda na fila
      if (!leftQueueRef.current) {
        void cancelOnDemandConsultation().catch(() => undefined);
        if (socket.connected) {
          socket.emit('cancelQueueRequest', { patientId: user.id });
        }
        leftQueueRef.current = true;
        sessionStorage.removeItem('onDemandTriage');
      }
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user, navigate]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleLogout = async () => {
    try {
      await leaveQueue(false);
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex">
      <Sidebar
        userRole={user.role}
        userName={user.name}
        onLogout={handleLogout}
      />

      <div className="flex-1 flex flex-col items-center justify-center p-8">
        <div className="w-full max-w-lg">
          <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 shadow-xl overflow-hidden">
            <div className="p-8 flex flex-col items-center text-center">
              {status === 'waiting' ? (
                <>
                  <div className="relative mb-8">
                    <div className="absolute inset-0 bg-teal-500/20 rounded-full animate-ping" />
                    <div className="relative p-6 bg-teal-50 dark:bg-teal-900/30 rounded-full">
                      <Loader2 className="w-12 h-12 text-teal-600 animate-spin" />
                    </div>
                  </div>

                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Buscando um médico...
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-sm">
                    Estamos conectando você ao primeiro médico disponível. Por favor, não feche esta página.
                  </p>

                  <div className="grid grid-cols-2 gap-4 w-full">
                    <div className="bg-gray-50 dark:bg-neutral-800 p-4 rounded-xl border border-gray-100 dark:border-neutral-700">
                      <Clock className="w-5 h-5 text-teal-600 mx-auto mb-2" />
                      <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Tempo de Espera</p>
                      <p className="text-xl font-bold text-gray-900 dark:text-white">{formatTime(timer)}</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-neutral-800 p-4 rounded-xl border border-gray-100 dark:border-neutral-700">
                      <Loader2 className="w-5 h-5 text-blue-600 mx-auto mb-2" />
                      <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Tempo Estimado</p>
                      <p className="text-xl font-bold text-gray-900 dark:text-white">~{estimatedTime} min</p>
                    </div>
                  </div>
                </>
              ) : status === 'matched' ? (
                <>
                  <div className="mb-8 p-6 bg-green-50 dark:bg-green-900/30 rounded-full">
                    <User className="w-12 h-12 text-green-600" />
                  </div>

                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Médico Encontrado!
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 mb-8">
                    Conectando você agora...
                  </p>

                  <div className="w-full p-4 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800 rounded-xl flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-green-200 dark:bg-green-700 flex items-center justify-center">
                      <User className="w-6 h-6 text-green-700 dark:text-green-200" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-bold text-gray-900 dark:text-white">{matchedDoctor?.name}</p>
                      <p className="text-xs text-green-600 dark:text-green-400 font-medium flex items-center gap-1">
                        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" /> Disponível agora
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <Loader2 className="w-10 h-10 text-teal-600 animate-spin mb-4" />
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Saindo da fila...</h2>
                </>
              )}
            </div>

            <div className="bg-gray-50 dark:bg-neutral-800/50 p-6 border-t border-gray-100 dark:border-neutral-800">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-gray-400 mt-0.5" />
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                  Ao sair da fila, você deixa de ser elegível para atendimento automático de plantão.
                  Verifique se sua câmera e microfone estão prontos antes de permanecer na espera.
                </p>
              </div>
            </div>
          </div>

          {status === 'waiting' && (
            <button
              onClick={() => leaveQueue(true)}
              disabled={isLeaving}
              className="mt-8 text-sm font-medium text-gray-500 hover:text-red-500 transition-colors flex items-center gap-2 mx-auto disabled:opacity-50"
            >
              {isLeaving ? 'Saindo...' : 'Sair da fila de espera'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
